import './style.css'
import { useState, useEffect, useRef, useMemo, useCallback } from 'react'
import Loader from "../Loader"
import { HighchartsReact } from 'highcharts-react-official'
import Highcharts from "highcharts/highstock"
import HighchartsMore from 'highcharts/highcharts-more'
import highchartsExporting from 'highcharts/modules/exporting';
import highchartsExportData from 'highcharts/modules/export-data';
import moment from 'moment'
import GraphFuncs from '../../utils/GraphFuncs'
import GlobalSVG from '../../utils/GlobalSVG'
import Tooltip from '../ToolTip'
import SlideToggle from '../SlideToggle'
import UnitToggle from '../UnitToggle'
import MultipleSelect from '../MultipleSelect'
import logo from "../../assets/images/logo_full_brand.png"

export default function UtilityBillGraph1(props) {

    highchartsExporting(Highcharts)
    highchartsExportData(Highcharts)

    const [unit, setUnit] = useState('price')
    const [displayedUtilities, setDisplayedUtilities] = useState([0, 1, 2])
    const [displayedTags, setDisplayedTags] = useState([])
    const [displayedTagBuildings, setDisplayedTagBuildings] = useState(props.buildings && Object.values(props.buildings).map(e => e.buildingInfo).map(e => e.building_id))
    const [filteredBuildings, setFilteredBuildings] = useState([])
    const [filteredTagBuildings, setFilteredTagBuildings] = useState([])
    const [andOr, setAndOr] = useState('and')
    const [displayOptions, setDisplayOptions] = useState({ eui: false, sqft: false })
    const [selectedBuilding, setSelectedBuilding] = useState(null)
    const [options, setOptions] = useState()
    const [currentData, setCurrentData] = useState(null)
    const [totals, setTotals] = useState({ price: { 0: 0, 1: 0, 2: 0 }, energy: { 0: 0, 1: 0, 2: 0 }, emissions: { 0: 0, 1: 0, 2: 0 } })
    const [buildingTotals, setBuildingTotals] = useState({})
    const [loading, setLoading] = useState(true)
    const chartRef = useRef(null)
    const unitDict = useMemo(() => ({ price: { 0: "$", 1: "$", 2: "$" }, energy: { 0: "GJ", 1: "GJ", 2: "GJ" }, emissions: { 0: "t CO₂e", 1: "t CO₂e", 2: "t CO₂e" }, consumption: { 0: "kWh", 1: "m³", 2: "L" } }), [])
    const utilityOptions = [{ idAttr: 0, utility: "Electricity" }, { idAttr: 1, utility: "Natural Gas" }, { idAttr: 2, utility: "Water" },]
    const [maxEui, setMaxEui] = useState(0)

    const calculateTotal = (bills, unit, displayedUtilities, divisor = 1) => {
        const conversions = {
            energy: { 0: (item) => item.kwh_consumption / 277.7778, 1: (item) => item.gas_consumption / 26.853 },
            emissions: { 0: (item) => item.emissions, 1: (item) => item.emissions },
            price: { 0: (item) => item.total_cost, 1: (item) => item.total_cost, 2: (item) => item.total_cost },
            consumption: { 0: (item) => item.kwh_consumption, 1: (item) => item.gas_consumption, 2: (item) => item.water_consumption },
        }

        return bills.reduce((total, item) => {
            if (!displayedUtilities.includes(item.type) || (unit === 'emissions' && item.type === 2)) return total
            const value = conversions[unit][item.type]?.(item) ?? 0
            return total + value / divisor
        }, 0)
    }

    const sortedList = useMemo(() => {
        return Object.keys(props.buildings).sort((a, b) => {
            const totalA = calculateTotal(Object.values(props.buildings[a].bills), unit, displayedUtilities)
            const totalB = calculateTotal(Object.values(props.buildings[b].bills), unit, displayedUtilities)
            return totalB - totalA
        })
    }, [props.buildings, unit, displayedUtilities])


    const tagOptions = useMemo(() => {
        if (!props.buildings) return new Set();

        const tempTagOptions = new Set();
        Object.values(props.buildings).forEach(building => {
            building.bills
                .flatMap(bill => bill.tags ?? [])
                .forEach(tag => tempTagOptions.add(tag));
        });
        return Array.from(tempTagOptions);
    }, [props.buildings]);

    const aggregateAccounts = (bills, unit, type, divisor, isSingleUtility) => {
        const accountsDict = bills.reduce((accounts, item) => {
            if (item.type !== type || (unit === 'emissions' && type === 2)) return accounts

            let value
            switch (unit) {
                case 'energy':
                    if (type === 0) value = item.kwh_consumption / 277.7778
                    else value = item.gas_consumption / 26.853
                    break

                case 'emissions':
                    value = item.emissions
                    break

                case 'consumption':
                    if (type === 0) value = item.kwh_consumption
                    else if (type === 1) value = item.gas_consumption
                    else value = item.water_consumption
                    break

                default:
                    value = item.total_cost
            }

            if (isSingleUtility && unit === 'price') {
                let acc
                switch (type) {
                    case 0: default:
                        acc = accounts[item.account_name] ?? {
                            total: 0, basic: 0, kva: 0, kwh: 0, carbon_charge: 0, taxes: 0, delivery: 0, other: 0
                        }
                        acc.total += value / divisor
                        acc.basic += item.basic_monthly / divisor
                        acc.kva += item.kva_cost / divisor
                        acc.kwh += item.kwh_cost / divisor
                        acc.delivery += item.delivery_charges / divisor
                        acc.other += item.other_charges / divisor
                        acc.carbon_charge += item.carbon_charge / divisor
                        acc.taxes += (item.municipal_tax + item.gst_cost + item.pst_cost) / divisor
                        break

                    case 1:
                        acc = accounts[item.account_name] ?? {
                            total: 0, basic: 0, gas: 0, carbon_charge: 0, taxes: 0, delivery: 0, other: 0
                        }
                        acc.total += value / divisor
                        acc.basic += item.basic_monthly / divisor
                        acc.gas += item.gas_cost / divisor
                        acc.delivery += item.delivery_charges / divisor
                        acc.other += item.other_charges / divisor
                        acc.carbon_charge += item.carbon_charge / divisor
                        acc.taxes += (item.municipal_tax + item.gst_cost + item.pst_cost) / divisor
                        break

                    case 2:
                        acc = accounts[item.account_name] ?? {
                            total: 0, basic: 0, water: 0, sewage: 0, carbon_charge: 0, taxes: 0, delivery: 0, other: 0
                        }
                        acc.total += value / divisor
                        acc.basic += item.basic_monthly / divisor
                        acc.water += item.water_cost / divisor
                        acc.sewage += item.sewage_cost / divisor
                        acc.delivery += item.delivery_charges / divisor
                        acc.other += item.other_charges / divisor
                        acc.taxes += (item.municipal_tax + item.gst_cost + item.pst_cost) / divisor
                        break


                }
                accounts[item.account_name] = acc
            } else {
                accounts[item.account_name] = (accounts[item.account_name] || 0) + value / divisor
            }
            return accounts
        }, {})
        const typDict = { 0: "Electrical Bill", 1: "Gas Bill", 2: "Water Bill" }
        return Object.entries(accountsDict).map(([accountName, data], ix) => ({
            name: `${typDict[type]} ${ix + 1}`,
            value: { accountName: accountName, y: isSingleUtility && unit === 'price' ? data.total : data, ...data },
        }))
    }

    function monthsBetween(startEnd) {
        const start = moment(startEnd[0]).startOf("month")
        const end = moment(startEnd[1]).startOf("month")
        const diff = end.diff(start, "months")
        return diff + 1
    }

    useEffect(() => {
        if (!sortedList || !props.buildings) return;

        const tempFilteredBuildings = sortedList.reduce((acc, key) => {
            const building = props.buildings[key];
            const bills = displayedTags?.length
                ? building.bills.filter(bill =>
                    andOr === 'and'
                        ? displayedTags.every(tag => bill.tags.includes(tag))
                        : displayedTags.some(tag => bill.tags.includes(tag))
                )
                : building.bills;
            if (!bills.length) return acc;

            acc.push({ ...building, bills });
            return acc;
        }, []);
        setFilteredBuildings(tempFilteredBuildings);
    }, [displayedTags, sortedList, props.buildings, andOr]);


    useEffect(() => {
        if (!filteredBuildings) return;

        const tempFilteredTagBuildings = filteredBuildings.map(e => e.buildingInfo.building_id).reduce((acc, key) => {
            const building = props.buildings[key];
            if (!displayedTagBuildings.includes(building.buildingInfo.building_id)) return acc;

            acc.push({ ...building });
            return acc;
        }, []);
        setFilteredTagBuildings(tempFilteredTagBuildings);
    }, [displayedTagBuildings, filteredBuildings, props.buildings]);

    const reset = () => {
        setDisplayedTagBuildings(props.buildings && Object.values(props.buildings).map(e => e.buildingInfo).map(e => e.building_id))
        setFilteredBuildings([])
        setFilteredTagBuildings([])
        setUnit('price')
        setAndOr('and')
        setDisplayedTags([])
        setDisplayOptions({ eui: false, sqft: false })
        setSelectedBuilding(null)
        setOptions()
        setCurrentData(null)
        setDisplayedUtilities([0, 1, 2])
    }



    useEffect(() => {
        if (!props.buildings) return

        let colors = ["#a3d237", "#4fa78b", "#006184", "#6e2b68", "#ffcf00", "#ff7500", "#db0661", "#007b4e", "#588C9E", "#007b4e"]
        setLoading(true)
        const namesDict = { 0: 'Electrical', 1: 'Natural Gas', 2: 'Water' }
        const tempTotal = { price: { 0: 0, 1: 0, 2: 0 }, energy: { 0: 0, 1: 0, 2: 0 }, emissions: { 0: 0, 1: 0, 2: 0 }, consumption: { 0: 0, 1: 0, 2: 0 } }
        const tempSeries = {}
        const tempBuildingTotals = {}
        let tempMax = 0
        for (const building of filteredTagBuildings) {
            if (displayOptions.sqft && !building.buildingInfo.sq_footage) continue
            const bills = building.bills
            const divisor = displayOptions.sqft ? building.buildingInfo.sq_footage * 0.092903 : 1
            tempBuildingTotals[building.buildingInfo.building] = 0

            if (displayedUtilities.length === 1) {
                const type = displayedUtilities[0]
                const accounts = aggregateAccounts(Object.values(bills), unit, type, divisor, true)
                accounts.forEach(({ name, value }, ix) => {
                    tempSeries[ix] = tempSeries[ix] || { name, values: [], type: 'column', axis: 0 }
                    tempSeries[ix].values.push({ name: building.buildingInfo.building, ...value })
                })
                tempTotal[unit][type] += calculateTotal(Object.values(bills), unit, [type], divisor)
                tempBuildingTotals[building.buildingInfo.building] += calculateTotal(Object.values(bills), unit, [type], divisor)
            } else {
                displayedUtilities.forEach((type) => {
                    const total = calculateTotal(Object.values(bills), unit, [type], divisor)
                    tempSeries[type] = tempSeries[type] || { name: namesDict[type], values: [], type: 'column', axis: 0 }
                    tempSeries[type].values.push({ name: building.buildingInfo.building, y: total })

                    tempTotal[unit][type] += total
                    tempBuildingTotals[building.buildingInfo.building] += total
                })
            }
            if (building.buildingInfo.sq_footage && displayOptions.eui) {
                tempSeries["eui"] = tempSeries["eui"] || { name: "EUI Actual", values: [], type: 'line', axis: 1 }
                let tempEui = calculateTotal(Object.values(bills), 'energy', [0, 1], building.buildingInfo.sq_footage * 0.092903) * (12 / monthsBetween(props.startEnd))
                tempSeries["eui"].values.push({ name: building.buildingInfo.building, y: calculateTotal(Object.values(bills), 'energy', [0, 1], building.buildingInfo.sq_footage * 0.092903) * (12 / monthsBetween(props.startEnd)) })
                tempSeries["eui_national"] = tempSeries["eui_national"] || { name: "Benchmark EUI", values: [], type: 'scatter', axis: 1 }
                tempSeries["eui_national"].values.push({
                    name: building.buildingInfo.building, y: (() => {
                        const euiValues = bills
                            .map(bill => bill.eui)
                            .filter(eui => typeof eui === 'number')
                        return euiValues.length > 0
                            ? (euiValues.reduce((sum, eui) => sum + eui, 0) / euiValues.length)
                            : 0
                    })()
                })
                let tempEui2 = (() => {
                    const euiValues = bills
                        .map(bill => bill.eui)
                        .filter(eui => typeof eui === 'number')
                    return euiValues.length > 0
                        ? (euiValues.reduce((sum, eui) => sum + eui, 0) / euiValues.length)
                        : 0
                })()
                if (tempEui2 > tempEui) tempEui = tempEui2
                if (tempEui > tempMax) tempMax = tempEui
            }
        }

        const tempData = Object.values(tempSeries).map((series, ix) => ({
            name: series.name,
            type: series.type,
            data: series.values,
            color: colors[ix],
            yAxis: series.axis,
            lineWidth: 0,
            marker: {
                symbol: 'triangle',
                lineWidth: 4,
                lineColor: colors[ix],
            }
        })) || [{
            name: '',
            data: [],
            color: colors[0],
            yAxis: 0,
        }]
        setTotals(tempTotal)
        setMaxEui(tempMax)
        setCurrentData(tempData)
        setBuildingTotals(tempBuildingTotals)
    }, [props.buildings, unit, displayedUtilities, displayOptions, props.startEnd, filteredTagBuildings])

    const handleSelectBuilding = useCallback((building) => {
        let tempBuilding = Object.values(props.buildings).filter(e => e.buildingInfo.building === building)
        if (tempBuilding.length) {
            setSelectedBuilding(tempBuilding[0].buildingInfo)
        }
    }, [props.buildings])

    useEffect(() => {
        if (!currentData) return
        if (typeof Highcharts === 'object') {
            HighchartsMore(Highcharts)
        }

        const sqftBuildings = filteredTagBuildings
            .filter(b => b.buildingInfo.sq_footage)
        const euiTotal = sqftBuildings.length > 0
            ? (sqftBuildings.reduce((sum, item) => sum += calculateTotal(Object.values(item.bills), 'energy', [0, 1], item.buildingInfo.sq_footage * 0.092903), 0))
            : 0

        const euiAverage = sqftBuildings.length > 0
            ? (euiTotal / sqftBuildings.length) * (12 / monthsBetween(props.startEnd))
            : 0

        setOptions({
            credits: {
                enabled: false,
            },
            chart: {
                events: {
                    load: function () {
                        const chart = this
                        chart.xAxis[0].ticks && Object.values(chart.xAxis[0].ticks).forEach(tick => {
                            if (tick.label) {
                                tick.label.element.style.cursor = 'pointer'
                                tick.label.element.onclick = () => {
                                    handleSelectBuilding(tick.label.textStr)
                                }
                            }
                        })
                    },
                    exportData: function (e) {
                        const round2 = n => Math.round((n + Number.EPSILON) * 100) / 100

                        e.dataRows.forEach(row => {
                            row.forEach((cell, j) => {
                                if (j === 0) return // category column

                                if (typeof cell === 'string' && /^-?\d+(\.\d+)?$/.test(cell.trim())) {
                                    cell = Number(cell)
                                }
                                if (typeof cell === 'number' && isFinite(cell)) {
                                    row[j] = round2(cell)
                                }
                            })
                        })
                    }
                },
                height: '50%',
                marginTop: 100,
            },
            legend: {
                enabled: true,
                align: 'left',
                layout: 'horizontal',
                verticalAlign: 'bottom',
                itemMarginBottom: 0,
                itemMarginTop: 0,
            },
            title: {
                text: '',
                align: 'left'
            },
            subtitle: {
                text: '',
                align: 'left'
            },
            xAxis: {
                type: 'category',
                min: 0,
                max: currentData && currentData.length !== 0 && currentData[0].data.length < 20 ? currentData[0].data.length - 1 : 20,
                accessibility: {
                    description: 'Building'
                },
                scrollbar: {
                    enabled: true
                }
            },
            yAxis: [{
                title: {
                    align: 'high',
                    offset: 15,
                    rotation: 0,
                    y: -15,
                    text: `${unitDict[unit][displayedUtilities[0]]}${displayOptions.sqft ? "/m²" : ""}`
                },
                labels: {
                    formatter: function () {
                        return this.value
                    }
                },
            },
            {
                title: {
                    align: 'high',
                    offset: 15,
                    rotation: 0,
                    y: -15,
                    text: 'EUI'
                },
                visible: displayOptions.eui,
                labels: {
                    formatter: function () {
                        return this.value
                    }
                },
                opposite: true,
                min: 0,
                max: Math.ceil(maxEui * 1.2),
                plotLines: [displayOptions.eui && {
                    value: sqftBuildings.length > 0
                        ? euiAverage
                        : 0,
                    color: 'red', // color of the line
                    width: 1, // thickness
                    dashStyle: 'Dash', // 'Solid', 'ShortDash', 'Dot', etc.
                    label: {
                        text: 'EUI (Average EUI of selected buildings)',
                        align: 'right',
                        style: {
                            color: 'red',
                            fontWeight: 'bold'
                        }
                    },
                    zIndex: 5
                }]
            }],
            tooltip: {
                formatter: function () {
                    const grandTotal = Object.values(totals[unit] || {}).reduce((acc, val) => acc + val, 0) || 1;
                    const useSqft = displayOptions.sqft;

                    // Helper to format numbers
                    const formatNumber = (value, forceDecimals = false) => {
                        const options = (useSqft || forceDecimals)
                            ? { minimumFractionDigits: 2, maximumFractionDigits: 2 }
                            : { maximumFractionDigits: 0 };
                        return value.toLocaleString(undefined, options);
                    };

                    // Helper to format value with unit
                    const formatValue = (value, forceDecimals = false) => {
                        const prefix = unit === 'price' ? unitDict[unit][displayedUtilities[0]] : '';
                        const suffix = unit !== 'price' ? unitDict[unit][displayedUtilities[0]] : '';
                        const sqftSuffix = useSqft ? '/m²' : '';
                        return `${prefix}${formatNumber(value, forceDecimals)}${suffix}${sqftSuffix}`;
                    };

                    // Helper to format with percentage
                    const formatWithPercent = (value, forceDecimals = false) => {
                        const percent = ((value / grandTotal) * 100).toLocaleString(undefined, {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2
                        });
                        return `${formatValue(value, forceDecimals)} (${percent}% of grand total)`;
                    };

                    // Building header
                    const buildingTotal = buildingTotals[this.point.name];
                    let html = `<b>${this.point.name}: ${formatWithPercent(buildingTotal)}</b><br/>`;

                    // Account details
                    if (this.point.accountName) {
                        html += `<b>${this.point.accountName}: ${formatWithPercent(this.y)}</b><br/>`;
                        // Electricity breakdown (if applicable)
                        if (displayedUtilities.length === 1 && unit === 'price') {
                            let charges
                            switch (displayedUtilities[0]) {
                                case 0:
                                    charges = [
                                        { label: 'Basic Monthly', value: this.point.basic },
                                        { label: 'kVA', value: this.point.kva },
                                        { label: 'kWh', value: this.point.kwh },
                                        { label: 'Delivery Charges', value: this.point.delivery },
                                        { label: 'Other Charges', value: this.point.other },
                                        { label: 'Carbon Charge', value: this.point.carbon_charge },
                                        { label: 'Taxes', value: this.point.taxes }
                                    ];
                                    break
                                case 1:
                                    charges = [
                                        { label: 'Basic Monthly', value: this.point.basic },
                                        { label: 'Gas Cost', value: this.point.gas },
                                        { label: 'Delivery Charges', value: this.point.delivery },
                                        { label: 'Other Charges', value: this.point.other },
                                        { label: 'Carbon Charge', value: this.point.carbon_charge },
                                        { label: 'Taxes', value: this.point.taxes }
                                    ];
                                    break
                                case 2:
                                    charges = [
                                        { label: 'Basic Monthly', value: this.point.basic },
                                        { label: 'Water Cost', value: this.point.water },
                                        { label: 'Sewage Cost', value: this.point.sewage },
                                        { label: 'Delivery Charges', value: this.point.delivery },
                                        { label: 'Other Charges', value: this.point.other },
                                        { label: 'Carbon Charge', value: this.point.carbon_charge },
                                        { label: 'Taxes', value: this.point.taxes }
                                    ];
                                    break
                                default:
                                    charges = []
                                    break
                            }
                            charges.forEach(({ label, value }) => {
                                html += `<b>${label}: $${formatNumber(value)}${useSqft ? '/m²' : ''}</b><br/>`;
                            });
                        }
                    }
                    // EUI metrics
                    else if (this.series.name === 'EUI Actual') {
                        html += `<b>${this.series.name}: ${formatNumber(this.y, true)} GJ/m²/year</b><br/>`;
                    }
                    else if (this.series.name === 'Benchmark EUI') {
                        const value = this.y === 0 ? 'N/A' : formatNumber(this.y, true);
                        html += `<b>${this.series.name}: ${value} GJ/m²/year</b><br/>`;
                    }
                    // Other series
                    else {
                        html += `<b>${this.series.name}: ${formatWithPercent(this.y)}</b><br/>`;
                    }

                    return html;
                }
            },
            plotOptions: {
                column: {
                    dataLabels: {
                        enabled: false
                    }
                },
                series: {
                    stacking: 'normal',
                },
            },
            series: currentData,
            exporting: {
                enabled: true,
                buttons: {
                    contextButton: {
                        menuItems: [
                            'viewFullscreen', 'separator', 'downloadCSV', 'downloadPNG', 'downloadPDF'
                        ]
                    },
                },
                sourceWidth: 1500,
                sourceHeight: 900,
                allowHTML: true,
                chartOptions: {
                    chart: {
                        marginTop: 250,
                        marginBottom: 120,
                        events: {
                            load: function () {
                                const chart = this;
                                const W = chart.chartWidth;
                                const H = chart.chartHeight;

                                // ── HEADER: white base ──
                                chart.renderer.rect(0, 0, W, 125)
                                    .attr({ fill: '#ffffff', zIndex: 4 })
                                    .add();

                                // ── HEADER: bold left accent bar ──
                                chart.renderer.rect(0, 0, 6, 125)
                                    .attr({ fill: '#007b4e', zIndex: 6 })
                                    .add();

                                // ── HEADER: diagonal decorative shape (top-right corner) ──
                                // SVG path: a parallelogram-ish slice in the top right
                                chart.renderer.path([
                                    'M', W - 220, 0,
                                    'L', W, 0,
                                    'L', W, 125,
                                    'L', W - 80, 125,
                                    'Z'
                                ])
                                    .attr({ fill: '#007b4e', opacity: 0.08, zIndex: 5 })
                                    .add();

                                chart.renderer.path([
                                    'M', W - 160, 0,
                                    'L', W, 0,
                                    'L', W, 125,
                                    'L', W - 30, 125,
                                    'Z'
                                ])
                                    .attr({ fill: '#a3d237', opacity: 0.13, zIndex: 5 })
                                    .add();

                                // ── HEADER: bottom border line ──
                                chart.renderer.rect(0, 125, W, 2)
                                    .attr({ fill: '#007b4e', zIndex: 6 })
                                    .add();

                                // ── LOGO ──                                
                                chart.renderer.image(logo, 22, 18, 210)
                                    .attr({ zIndex: 7 })
                                    .add();

                                // ── REPORT TITLE ──
                                chart.renderer.text('Portfolio Utility Report', 22, 90)
                                    .attr({ align: 'left', zIndex: 7 })
                                    .css({
                                        color: '#1a1a1a',
                                        fontSize: '13px',
                                        fontWeight: '600',
                                        letterSpacing: '0.12em',
                                        textTransform: 'uppercase',
                                    })
                                    .add();

                                // ── DATE RANGE ──
                                const dateRangeText = props.startEnd
                                    ? `${moment(props.startEnd[0]).format("MMM DD, YYYY")}  →  ${moment(props.startEnd[1]).format("MMM DD, YYYY")}   ·   ${GraphFuncs.getDifferenceInDays(props.startEnd[0], props.startEnd[1])} days`
                                    : '';
                                chart.renderer.text(dateRangeText, 22, 110)
                                    .attr({ align: 'left', zIndex: 7 })
                                    .css({ color: '#999999', fontSize: '11px' })
                                    .add();

                                // ── TOTALS: setup ──
                                const prefix = unit === 'price' ? unitDict[unit][displayedUtilities[0]] : '';
                                const suffix = unit !== 'price' ? ` ${unitDict[unit][displayedUtilities[0]]}` : '';
                                const sqftSuffix = displayOptions.sqft ? ' /m²' : '';
                                const fmtOpts = displayOptions.sqft
                                    ? { minimumFractionDigits: 2, maximumFractionDigits: 2 }
                                    : { maximumFractionDigits: 0 };

                                const grandTotal = displayedUtilities
                                    .reduce((sum, type) => sum + (totals[unit][type] || 0), 0)
                                    .toLocaleString(undefined, fmtOpts);

                                const totalsItems = [
                                    {
                                        label: 'Grand Total',
                                        value: `${prefix}${grandTotal}${suffix}${sqftSuffix}`,
                                        accent: '#007b4e',
                                        highlight: true,
                                    },
                                ];

                                if (!['eui', 'consumption'].includes(unit)) {
                                    totalsItems.push({
                                        label: 'Electrical',
                                        value: `${prefix}${(totals[unit][0] || 0).toLocaleString(undefined, fmtOpts)}${suffix}${sqftSuffix}`,
                                        accent: '#a3d237',
                                    });
                                    totalsItems.push({
                                        label: 'Natural Gas',
                                        value: `${prefix}${(totals[unit][1] || 0).toLocaleString(undefined, fmtOpts)}${suffix}${sqftSuffix}`,
                                        accent: '#4fa78b',
                                    });
                                }

                                if (!['eui', 'energy', 'emissions', 'consumption'].includes(unit)) {
                                    totalsItems.push({
                                        label: 'Water',
                                        value: `${prefix}${(totals[unit][2] || 0).toLocaleString(undefined, fmtOpts)}${suffix}${sqftSuffix}`,
                                        accent: '#006184',
                                    });
                                }

                                // ── TOTALS: right-side block ──
                                const blockW = 220;
                                const blockGap = 16;
                                const totalBlocksWidth = totalsItems.length * blockW + (totalsItems.length - 1) * blockGap;
                                const startX = W - totalBlocksWidth - 30;

                                totalsItems.forEach((item, i) => {
                                    const x = startX + i * (blockW + blockGap);

                                    // card background
                                    chart.renderer.rect(x, 14, blockW, 100, 3)
                                        .attr({ fill: item.highlight ? '#007b4e' : '#f7f7f7', zIndex: 6 })
                                        .add();

                                    // top accent bar on each card
                                    chart.renderer.rect(x, 14, blockW, 3, 0)
                                        .attr({ fill: item.accent, zIndex: 7 })
                                        .add();

                                    // label
                                    chart.renderer.text(item.label.toUpperCase(), x + 14, 50)
                                        .attr({ zIndex: 8 })
                                        .css({
                                            color: item.highlight ? 'rgba(255,255,255,0.75)' : '#aaaaaa',
                                            fontSize: '9px',
                                            fontWeight: '700',
                                            letterSpacing: '0.1em',
                                        })
                                        .add();

                                    // value
                                    chart.renderer.text(item.value, x + 14, 90)
                                        .attr({ zIndex: 8 })
                                        .css({
                                            color: item.highlight ? '#ffffff' : '#1a1a1a',
                                            fontSize: '20px',
                                            fontWeight: 'bold',
                                        })
                                        .add();
                                });

                                // ── FOOTER ──
                                // main footer strip
                                chart.renderer.rect(0, H - 58, W, 58)
                                    .attr({ fill: '#f9f9f9', zIndex: 4 })
                                    .add();

                                // top border: thick green + thin lime
                                chart.renderer.rect(0, H - 60, W, 3)
                                    .attr({ fill: '#007b4e', zIndex: 5 })
                                    .add();
                                chart.renderer.rect(0, H - 57, W, 1)
                                    .attr({ fill: '#a3d237', zIndex: 5 })
                                    .add();

                                // left accent block in footer
                                chart.renderer.rect(0, H - 58, 6, 58)
                                    .attr({ fill: '#007b4e', zIndex: 6 })
                                    .add();

                                // exported on
                                chart.renderer.text(
                                    `Generated ${moment().format("MMM DD, YYYY [at] h:mm A")}`,
                                    22, H - 5
                                )
                                    .attr({ align: 'left', zIndex: 6 })
                                    .css({ color: '#aaaaaa', fontSize: '10px' })
                                    .add();

                                // center dot separator line
                                chart.renderer.text('CONFIDENTIAL  ·  FOR INTERNAL USE ONLY', W / 2, H - 5)
                                    .attr({ align: 'center', zIndex: 6 })
                                    .css({
                                        color: '#cccccc',
                                        fontSize: '9px',
                                        fontWeight: '700',
                                        letterSpacing: '0.12em',
                                    })
                                    .add();

                                // right copyright
                                chart.renderer.text(`© ${new Date().getFullYear()} All rights reserved`, W - 22, H - 5)
                                    .attr({ align: 'right', zIndex: 6 })
                                    .css({ color: '#aaaaaa', fontSize: '10px' })
                                    .add();
                            }
                        }
                    }
                }
            },
        })

        setLoading(false)
    }, [currentData, unit, totals, buildingTotals, displayedUtilities, props.buildings, displayOptions, handleSelectBuilding, maxEui, unitDict, props.startEnd, filteredTagBuildings])

    const handleChangeUnit = (e) => {
        if (e.currentTarget.value === 'consumption') setDisplayedUtilities([2])
        setUnit(e.currentTarget.value)
    }


    const handleSelectedUtility = (e) => {
        let val = e.target.value

        if (unit === "consumption") {
            setDisplayedUtilities(prev => prev.includes(val) ? [] : [val])
            return
        }

        if (val === 'all') {
            setDisplayedUtilities(utilityOptions.map(e => e.idAttr))
            return
        }

        if (displayedUtilities.includes(val)) {
            setDisplayedUtilities(prev => prev.filter(v => v !== val))
        } else {
            setDisplayedUtilities(prev => [...prev, val])
        }
    }

    const handleSelectedTag = (e) => {
        let val = e.target?.value
        if (!val) return
        if (val === 'all') {
            setDisplayedTags([])
            return
        }
        if (displayedTags.includes(val)) {
            setDisplayedTags(prev => prev.filter(v => v !== val))
        } else {
            setDisplayedTags(prev => [...prev, val])
        }
    }

    const handleTagBuildings = (e) => {
        let val = e.target?.value
        if (!val) return
        if (val === 'all') {
            setDisplayedTagBuildings(filteredBuildings.map(e => e.buildingInfo).map(b => b.building_id))
            return
        }
        if (displayedTagBuildings.includes(val)) {
            setDisplayedTagBuildings(prev => prev.filter(v => v !== val))
        } else {
            setDisplayedTagBuildings(prev => [...prev, val])
        }
    }

    useEffect(() => {
        handleSelectedTag(andOr)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [andOr])

    return (
        <div>
            <div className={`card tablet:p-8 mobile:p-4 rounded-lg relative mt-8`}>
                <div className='flex flex-nowrap justify-between items-center'>
                    <div>
                        <h2 className="capitalize w-full laptop:text-3xl tablet:text-xl   font-bold text-[#332D41] md:decoration-dashed">Portfolio Utility Report</h2>
                        {props.startEnd && <p className='mb-4 font-light text-sm'>Date Range: <span className='font-normal'>{moment(props.startEnd[0]).format("MMM DD, YYYY")} - {moment(props.startEnd[1]).format("MMM DD, YYYY")} ({GraphFuncs.getDifferenceInDays(props.startEnd[0], props.startEnd[1])} days)</span></p>}
                        {props.compare && props.compareStartEnd.length === 2 && <p className='mb-4 font-light text-sm'>Comparative Range: <span className='font-normal'>{moment(props.compareStartEnd[0]).format("MMM DD, YYYY")} - {moment(props.compareStartEnd[1]).format("MMM DD, YYYY")} ({GraphFuncs.getDifferenceInDays(props.compareStartEnd[0], props.compareStartEnd[1])} days)</span></p>}
                    </div>
                    <div className="flex flex-nowrap justify-center items-center">
                        <select className='w-1/2 p-2 border border-1 rounded  border-[#C1C1C1] text-[#007b4e] w-[220px]' value={unit} onChange={handleChangeUnit}>
                            <option value="price">Cost ($)</option>
                            <option value="energy">Energy (GJ)</option>
                            <option value="emissions">Emissions (t CO₂e)</option>
                            <option value="consumption">Consumption per Utility</option>
                        </select>
                        {displayOptions.sqft &&
                            <Tooltip
                                text="Buildings with no square footage info will not show values"
                                right="0"
                                bottom="100%"
                                left="-500%"
                                width="200px"
                                show={true}>
                                <div className="ml-2">
                                    {GlobalSVG.info('black', '15', '15')}
                                </div>
                            </Tooltip>}
                    </div>
                </div>
                <div className="flex flex-nowrap items-start justify-between w-full">
                    <div>
                        <h2 className='mb-2 font-medium mt-4'>Grand Total: {unit === 'price' ? unitDict[unit][displayedUtilities[0]] : ""}{(displayedUtilities.reduce((total, item) => {
                            return total += totals[unit][item]
                        }, 0)).toLocaleString(undefined,
                            displayOptions.sqft
                                ? { minimumFractionDigits: 2, maximumFractionDigits: 2 }
                                : { maximumFractionDigits: 0 })} {unit !== 'price' ? unitDict[unit][displayedUtilities[0]] : ""}{displayOptions.sqft ? " /m²" : ""}</h2>
                        {!['eui', 'consumption'].includes(unit) && <div className='flex flex-nowrap items-center'>
                            Electrical: {unit === 'price' ? unitDict[unit][displayedUtilities[0]] : ""}{(totals[unit][0]).toLocaleString(undefined,
                                displayOptions.sqft
                                    ? { minimumFractionDigits: 2, maximumFractionDigits: 2 }
                                    : { maximumFractionDigits: 0 })} {unit !== 'price' ? unitDict[unit][displayedUtilities[0]] : ""}{displayOptions.sqft ? " /m²" : ""}
                            {unit === 'energy' &&
                                <Tooltip
                                    text="1 GJ = 277.7778 kWh"
                                    right="0"
                                    left="400%"
                                    width="300px"
                                    show={true}>
                                    <div className="ml-2">
                                        {GlobalSVG.info('black', '15', '15')}
                                    </div>
                                </Tooltip>}
                        </div>}
                        {!['eui', 'consumption'].includes(unit) && <div className='flex flex-nowrap items-center'>
                            Natural Gas: {unit === 'price' ? unitDict[unit][displayedUtilities[0]] : ""}{(totals[unit][1]).toLocaleString(undefined,
                                displayOptions.sqft
                                    ? { minimumFractionDigits: 2, maximumFractionDigits: 2 }
                                    : { maximumFractionDigits: 0 })} {unit !== 'price' ? unitDict[unit][displayedUtilities[0]] : ""}{displayOptions.sqft ? " /m²" : ""}
                            {unit === 'energy' &&
                                <Tooltip
                                    text="1 GJ = 947.81714 cubic feet of natural gas<br />1 GJ = 26.853 cubic metres of natural gas"
                                    right="0"
                                    left="400%"
                                    width="300px"
                                    show={true}>
                                    <div className="ml-2">
                                        {GlobalSVG.info('black', '15', '15')}
                                    </div>
                                </Tooltip>}
                        </div>}
                        {!['eui', 'energy', 'emissions', 'consumption'].includes(unit) && <div className='flex flex-nowrap items-center'>
                            Water: {unit === 'price' ? unitDict[unit][displayedUtilities[0]] : ""}{(totals[unit][2]).toLocaleString(undefined,
                                displayOptions.sqft
                                    ? { minimumFractionDigits: 2, maximumFractionDigits: 2 }
                                    : { maximumFractionDigits: 0 })} {unit !== 'price' ? unitDict[unit][displayedUtilities[0]] : ""}{displayOptions.sqft ? " /m²" : ""}
                        </div>}
                    </div>
                    <div>
                        <MultipleSelect reports={true} data={utilityOptions} idAttr="idAttr" valAttr="utility" selectItem={handleSelectedUtility} selectedList={displayedUtilities} placeholder="Select Utilities" />
                        <MultipleSelect reports={true} data={tagOptions} idAttr="idAttr" valAttr="tag" selectItem={handleSelectedTag} selectedList={displayedTags} placeholder="Select Tags" />
                        <MultipleSelect reports={true} data={filteredBuildings.map(e => e.buildingInfo)} idAttr="building_id" valAttr="building" selectItem={handleTagBuildings} selectedList={displayedTagBuildings} placeholder="Select Buildings" />
                        <UnitToggle setOptions={setAndOr} options={andOr} option1="and" option2="or" button1="And" button2="Or" info='Joining tags by "And" vs "Or"' />
                        <SlideToggle setOptions={setDisplayOptions} disabled={monthsBetween(props.startEnd) < 12} titleClass={monthsBetween(props.startEnd) < 12 ? "text-[grey]" : ''} info={monthsBetween(props.startEnd) < 12 ? "Selected period must be equal to or greater than 12 months." : ""} options={displayOptions} id={"eui"} name={"EUI"} />
                        <SlideToggle setOptions={setDisplayOptions} options={displayOptions} id={"sqft"} name={"Per Square Metre"} />
                        <div className='flex flex-nowrap items-between justify-end mt-4 mr-4'>
                            <button className='text-[#007b4e]' onClick={reset}>
                                Reset
                            </button>
                        </div>
                    </div>
                </div>
                <div className="flex flex-nowrap tablet:justify-end laptop:justify-start mobile:justify-end items-center rounded-lg min-h-[600px]">
                    <div className={`col-span-2 w-full`}>
                        {options && <HighchartsReact
                            highcharts={Highcharts}
                            options={options}
                            ref={chartRef}
                            immutable={true}
                        />}
                        {loading && <Loader />}
                    </div>
                </div>
            </div>
            {selectedBuilding &&
                <div className='fixed w-screen h-screen top-0 left-0 modal-background flex flex-nowrap justify-center items-center alert-detail-modal'>
                    <div className='card w-[80vw] h-[75vh] m-2 p-[40px] pt-[0] background-white rounded-md overflow-auto' onClick={(e) => { e.stopPropagation() }}>
                        <div className="flex flex-nowrap justify-end items-center pt-[20px] w-full">
                            <div className='cursor-pointer ml-3' onClick={() => setSelectedBuilding(null)}>{GlobalSVG.closeXBig('black')}</div>
                        </div>
                        <div className="flex flex-nowrap items-center justify-between text-3xl p-2 w-full">
                            <div className='items-center mb-2 w-full'>
                                <input
                                    placeholder='Building Name'
                                    className='text-3xl p-2 outline-none hide-input-background w-full'
                                    type="text"
                                    name='building'
                                    disabled={true}
                                    defaultValue={selectedBuilding.building ? selectedBuilding.building : ""}
                                />
                            </div>
                            <div className='flex flex-wrap w-1/2 items-center pt-3 pb-3 mb-6'>
                                <div className='w-full flex flex-wrap items-center'>
                                    <img className='w-20' src={selectedBuilding.image ? selectedBuilding.image : require('../../assets/images/placeholder-image.png')} alt="" />
                                </div>
                            </div>
                        </div>
                        <div>
                            <div className='flex flex-wrap'>
                                <div className='input-border flex flex-wrap w-1/2 items-center pt-3 pb-3 mb-6'>
                                    <div className='w-full flex flex-wrap items-center'>
                                        <label className='mobile:w-1/2 tablet:w-4/12 text-base input-label-color' htmlFor="address">Address</label>
                                        <input
                                            placeholder='Address'
                                            className='mobile:w-1/2 tablet:w-7/12 mr-2 text-base input-color p-2 outline-none hide-input-background'
                                            type="text"
                                            name="address" defaultValue={selectedBuilding.address ? selectedBuilding.address : ""}
                                            disabled={true}
                                        />
                                    </div>
                                </div>
                                <div className='input-border flex flex-wrap w-1/2 items-center pt-3 pb-3 mb-6'>
                                    <div className='w-full flex flex-wrap items-center'>
                                        <label className='mobile:w-1/2 tablet:w-4/12 text-base input-label-color' htmlFor="city">City</label>
                                        <input
                                            placeholder='City'
                                            className='mobile:w-1/2 tablet:w-7/12 mr-2 text-base input-color p-2 outline-none hide-input-background'
                                            type="text"
                                            name="city" defaultValue={selectedBuilding.city ? selectedBuilding.city : ""}
                                            disabled={true}
                                        />
                                    </div>
                                </div>
                            </div>
                            <div className='flex flex-wrap'>
                                <div className='input-border flex flex-wrap w-1/2 items-center pt-3 pb-3 mb-6'>
                                    <div className='w-full flex flex-wrap items-center'>
                                        <label className='mobile:w-1/2 tablet:w-4/12 text-base input-label-color' htmlFor="province">Province</label>
                                        <input
                                            placeholder='Province'
                                            className='mobile:w-1/2 tablet:w-7/12 mr-2 text-base input-color p-2 outline-none hide-input-background'
                                            type="text"
                                            name="province" defaultValue={selectedBuilding.province ? selectedBuilding.province : ""}
                                            disabled={true}
                                        />
                                    </div>
                                </div>
                                <div className='input-border flex flex-wrap w-1/2 items-center pt-3 pb-3 mb-6'>
                                    <div className='w-full flex flex-wrap items-center'>
                                        <label className='mobile:w-1/2 tablet:w-4/12 text-base input-label-color' htmlFor="postalcode">Postal Code</label>
                                        <input
                                            placeholder='Postal Code'
                                            className='mobile:w-1/2 tablet:w-7/12 mr-2 text-base input-color p-2 outline-none hide-input-background'
                                            type="text"
                                            name="postalcode" defaultValue={selectedBuilding.postalcode ? selectedBuilding.postalcode : ""}
                                            disabled={true}
                                        />
                                    </div>
                                </div>
                                <div className='flex flex-wrap w-full'>
                                    <div className='input-border flex flex-wrap w-1/2 items-center pt-3 pb-3 mb-6'>
                                        <div className='w-full flex flex-wrap items-center'>
                                            <label className='mobile:w-1/2 tablet:w-4/12 text-base input-label-color' htmlFor="country">Country</label>
                                            <input
                                                placeholder='Country'
                                                className='mobile:w-1/2 tablet:w-7/12 mr-2 text-base input-color p-2 outline-none hide-input-background'
                                                type="text"
                                                name="country" defaultValue={selectedBuilding.country ? selectedBuilding.country : ""}
                                                disabled={true}
                                            />
                                        </div>
                                    </div>
                                    <div className='input-border flex flex-wrap w-1/2 items-center pt-3 pb-3 mb-6'>
                                        <div className='w-full flex flex-wrap items-center'>
                                            <div className='w-full flex flex-wrap items-center'>
                                                <label className='mobile:w-1/2 tablet:w-4/12 text-base input-label-color' htmlFor="timezone">Timezone</label>
                                                <input
                                                    placeholder='Timezone'
                                                    className='mobile:w-1/2 tablet:w-7/12 mr-2 text-base input-color p-2 outline-none hide-input-background'
                                                    type="text"
                                                    name="timezone" defaultValue={selectedBuilding.timezone ? selectedBuilding.timezone : ""}
                                                    disabled={true}
                                                />
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div className='flex flex-wrap w-full'>
                                    <div className='input-border flex flex-wrap w-1/2 items-center pt-3 pb-3 mb-6'>
                                        <div className='w-full flex flex-wrap items-center'>
                                            <label className='mobile:w-1/2 tablet:w-4/12 text-base input-label-color' htmlFor="sq_footage">Square Footage</label>
                                            <input
                                                placeholder='Square Footage'
                                                className='mobile:w-1/2 tablet:w-7/12 mr-2 text-base input-color p-2 outline-none hide-input-background'
                                                type="number"
                                                step="1"
                                                name="sq_footage"
                                                defaultValue={selectedBuilding.sq_footage ? selectedBuilding.sq_footage : ""}
                                                disabled={true}
                                            />
                                        </div>
                                    </div>
                                    <div className='input-border flex flex-wrap w-1/2 items-center pt-3 pb-3 mb-6'>
                                        <div className='w-full flex flex-wrap items-center'>
                                            <div className='w-full flex flex-wrap items-center'>
                                                <label className='mobile:w-1/2 tablet:w-4/12 text-base input-label-color' htmlFor="building_type">EUI Building Type</label>
                                                <input
                                                    placeholder='EUI Building Type'
                                                    className='mobile:w-1/2 tablet:w-7/12 mr-2 text-base input-color p-2 outline-none hide-input-background'
                                                    type="text"
                                                    name="building_type" defaultValue={selectedBuilding.building_type ? selectedBuilding.building_type : ""}
                                                    disabled={true}
                                                />
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div className='flex flex-wrap w-full'>
                                    <div className='input-border flex flex-wrap w-1/2 items-center pt-3 pb-3 mb-6'>
                                        <div className='w-full flex flex-wrap items-center'>
                                            <label className='mobile:w-1/2 tablet:w-4/12 text-base input-label-color' htmlFor="rep_name">Rep. Name</label>
                                            <input
                                                placeholder='Rep. Name'
                                                className='mobile:w-1/2 tablet:w-7/12 mr-2 text-base input-color p-2 outline-none hide-input-background'
                                                type="text"
                                                name="rep_name" defaultValue={selectedBuilding.rep_name ? selectedBuilding.rep_name : ""}
                                                disabled={true}
                                            />

                                        </div>
                                    </div>
                                    <div className='input-border flex flex-wrap w-1/2 items-center pt-3 pb-3 mb-6'>
                                        <div className='w-full flex flex-wrap items-center'>
                                            <div className='w-full flex flex-wrap items-center'>
                                                <label className='mobile:w-1/2 tablet:w-4/12 text-base input-label-color' htmlFor="rep_phone">Rep. Phone</label>
                                                <input
                                                    placeholder='Rep. Phone'
                                                    className='mobile:w-1/2 tablet:w-7/12 mr-2 text-base input-color p-2 outline-none hide-input-background'
                                                    type="text"
                                                    name="rep_phone" defaultValue={selectedBuilding.rep_phone ? selectedBuilding.rep_phone : ""}
                                                    disabled={true}
                                                />
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div className='flex flex-wrap w-full'>
                                    <div className='input-border flex flex-wrap w-1/2 items-center pt-3 pb-3 mb-6'>
                                        <div className='w-full flex flex-wrap items-center'>
                                            <label className='mobile:w-1/2 tablet:w-4/12 text-base input-label-color' htmlFor="rep_email">Rep. Email</label>
                                            <input
                                                placeholder='Rep. Email'
                                                className='mobile:w-1/2 tablet:w-7/12 mr-2 text-base input-color p-2 outline-none hide-input-background'
                                                type="text"
                                                name="rep_email" defaultValue={selectedBuilding.rep_email ? selectedBuilding.rep_email : ""}
                                                disabled={true}
                                            />
                                        </div>
                                    </div>
                                    <div className='input-border flex flex-wrap w-1/2 items-center pt-3 pb-3 mb-6'>
                                        <div className='w-full flex flex-wrap items-center'>
                                            <div className='w-full flex flex-wrap items-center'>
                                                <label className='mobile:w-1/2 tablet:w-4/12 text-base input-label-color' htmlFor="image">Rep. Image</label>

                                                <img className='w-20' src={selectedBuilding.rep_image ? selectedBuilding.rep_image : require('../../assets/images/placeholder-image.png')} alt="" />
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div className='flex flex-wrap w-full'>
                                    <div className='input-border flex flex-wrap w-full items-center pt-3 pb-3 mb-6'>
                                        <div className='w-full'>
                                            <p className='w-full text-base input-label-color' htmlFor="buildings">Notes</p>
                                            <textarea className="w-full" name="notes" >
                                                {selectedBuilding.notes}
                                            </textarea>
                                        </div>
                                    </div>
                                </div>
                            </div>

                        </div>
                    </div>
                </div>
            }
        </div>
    )
}